import { api, state, toast, inspectorForm, modalConfirm } from '../state.js';
import { esc, badge, formatDateTime, debounce } from '/core/js/utils.js';
import { icon } from '../icons.js';
import { mountPageHeader, errorState, chipsHtml } from './ui/index.js';
import '/core/js/components/password-meter.js';

/**
 * Who may use this console, and as what (chunk 4.8b; U8 for the table, the
 * filters and the per-community level).
 *
 * Every directory person who signs in to account settings gets a row here
 * with the User role, so the list opens on everyone, with the Role filter a
 * click away. Directory people are given a role here; their name, email and
 * password are the directory's. A local account exists only in ARC — the
 * break-glass path for when the directory cannot be reached.
 *
 * A User's communities are set in the drawer, one row per community with a
 * LEVEL — None · ARB view · ARB manage · Parcels · Manage — each a fixed set
 * of the four site permissions (CommunityManage rolls the others up, so the
 * stored set is what the level says). A set that is not one of the five
 * (ParcelManage + ARBManage, from the beta) shows as "custom" beside the
 * row and is kept as it is until a level is picked.
 *
 * The API enforces the guardrails (no changing your own role, no blocking or
 * deleting yourself, only a Super Admin touches a Super Admin); the drawer
 * only avoids offering what it would refuse.
 */
const ROLES = {
  user:       { label: 'User',        badge: 'none',
                does: 'The communities they are given below, with the rights set there.' },
  admin:      { label: 'Admin',       badge: 'active',
                does: 'Every community, the data model, the palette dictionary, users and the audit log.' },
  superadmin: { label: 'Super Admin', badge: 'info',
                does: 'Everything an Admin can do, plus Access and Settings, and other Super Admins.' },
};
const RANK = { superadmin: 0, admin: 1, user: 2 };

export const LEVELS = {
  none:    { label: 'None',       perms: [],                                                           title: 'Not a member of this community' },
  arbview: { label: 'ARB view',   perms: ['ARBView'],                                                  title: 'The ARB map, read-only' },
  arb:     { label: 'ARB manage', perms: ['ARBManage', 'ARBView'],                                     title: 'Review and approve on the ARB map' },
  parcels: { label: 'Parcels',    perms: ['ParcelManage'],                                             title: 'The Parcels map: lots, dwellings and website data' },
  manage:  { label: 'Manage',     perms: ['CommunityManage', 'ParcelManage', 'ARBManage', 'ARBView'], title: 'Manage the community: everything in it' },
};
const SHORT = { CommunityManage: 'Manage', ParcelManage: 'Parcels', ARBManage: 'ARB manage', ARBView: 'ARB view' };

/** Which level a stored permission set is, or 'custom' when it is none of the five. */
export function levelOf(perms) {
  const has = new Set(perms ?? []);
  if (!has.size) return 'none';
  if (has.has('CommunityManage')) return 'manage';
  if (has.has('ParcelManage') && !has.has('ARBManage') && !has.has('ARBView')) return 'parcels';
  if (has.has('ARBManage') && !has.has('ParcelManage')) return 'arb';
  if (has.has('ARBView') && has.size === 1) return 'arbview';
  return 'custom';
}
const levelLabel = (perms) => { const l = levelOf(perms); return l === 'custom' ? perms.map(p => SHORT[p] ?? p).join(' + ') : LEVELS[l].label; };

const roleBadge = (role) => badge(ROLES[role]?.badge ?? 'none', ROLES[role]?.label ?? role);

class ArcUsers extends HTMLElement {
  #items = [];
  #memberships = new Map();     // account id → [{ site_id, slug, name, permissions }]
  #q = '';
  #filters = { role: '', site: '', source: '' };

  get #me() { return state.user ?? {}; }
  get #isSuper() { return !!this.#me.is_super_admin; }

  connectedCallback() {
    this.innerHTML = `
      <div data-slot="header"></div>
      <arc-toolbar></arc-toolbar>
      <div class="card"><arc-data-table></arc-data-table></div>`;
    mountPageHeader(this.querySelector('[data-slot="header"]'), {
      title: 'Users', context: 'System · people',
      sub: 'Who can use ARC. A User opens the communities they are given, with the level set there; an Admin opens every community and the configuration.',
      actions: `<button type="button" class="btn btn-secondary" data-act="local">${icon('user')}Add local account</button>
        ${this.#me.ldap_enabled ? `<button type="button" class="btn btn-primary" data-act="import">${icon('users')}Add from the directory</button>` : ''}`,
    });
    this.querySelector('arc-toolbar').configure({
      search: { placeholder: 'Search login, name or email' },
      filters: [
        { name: 'role', label: 'Role', all: 'All roles', options: [{ value: 'admins', label: 'Admins' }, { value: 'superadmin', label: 'Super Admins' }, { value: 'user', label: 'Users' }] },
        { name: 'site', label: 'Community', all: 'All communities', options: (state.sites ?? []).map(s => ({ value: String(s.id), label: s.name })) },
        { name: 'source', label: 'Source', all: 'Any source', options: [{ value: 'directory', label: 'Directory' }, { value: 'local', label: 'Local' }] },
      ],
      count: '',
    });
    this.addEventListener('change', e => {
      if (!e.target.matches('arc-toolbar')) return;
      this.#q = e.detail.search.toLowerCase();
      this.#filters = { role: e.detail.filters.role ?? '', site: e.detail.filters.site ?? '', source: e.detail.filters.source ?? '' };
      this.#render();
    });
    this.addEventListener('click', e => {
      const b = e.target.closest('[data-act]');
      if (!b) return;
      if (b.dataset.act === 'local') this.#createLocal();
      if (b.dataset.act === 'import') this.#importFromDirectory();
    });
    this.addEventListener('row-click', e => this.#open(e.detail.row.id));
    this.querySelector('arc-data-table').loading = true;
    this.#load();
  }

  async #load() {
    try {
      if (!state.sites?.length) { const r = await api.sites.list(); state.sites = r.items ?? []; }
      const [users, members] = await Promise.all([api.users.list(), api.users.memberships()]);
      this.#items = users;
      this.#memberships = new Map();
      for (const m of members.items ?? []) {
        if (!this.#memberships.has(m.account_id)) this.#memberships.set(m.account_id, []);
        this.#memberships.get(m.account_id).push(m);
      }
      this.#render();
      // #/users?open=<login> opens that person, once (the Cleanup page links here).
      const open = this.ctx?.query?.open;
      const target = open && this.ctx.opened !== open && this.#items.find(u => u.login === open);
      if (target) {
        this.ctx.opened = open;
        this.#open(target.id);
      }
    } catch (err) {
      this.querySelector('.card').innerHTML = errorState(err, 'Could not load users');
    }
  }

  #sitesOf(u) { return this.#memberships.get(u.id) ?? []; }

  #visible() {
    const { role, site, source } = this.#filters;
    return this.#items.filter(u =>
      (role === '' || (role === 'admins' ? u.role !== 'user' : u.role === role))
      && (source === '' || (source === 'local') === !u.ldap_imported)
      && (site === '' || u.role !== 'user' || this.#sitesOf(u).some(m => String(m.site_id) === site))
      && (!this.#q || [u.login, u.full_name, u.email].some(v => String(v ?? '').toLowerCase().includes(this.#q))));
  }

  #render() {
    const rows = this.#visible();
    const me = this.#me;
    this.querySelector('arc-toolbar').count = `${rows.length} of ${this.#items.length} ${this.#items.length === 1 ? 'person' : 'people'}`;
    this.querySelector('arc-data-table').configure({
      columns: [
        { key: 'login', label: 'Login', sortable: true, render: u => `<strong>${esc(u.login)}</strong>${u.id === me.id ? ` ${badge('info', 'you')}` : ''}` },
        { key: 'full_name', label: 'Name', sortable: true },
        { key: 'role', label: 'Role', sortable: true, get: u => RANK[u.role] ?? 9, render: u => roleBadge(u.role) },
        { key: 'sites', label: 'Communities', sortable: true, fill: true, get: u => (u.role !== 'user' ? 99 : this.#sitesOf(u).length), render: u => this.#sitesCell(u) },
        { key: 'source', label: 'Source', sortable: true, get: u => (u.ldap_imported ? 'Directory' : 'Local') },
        { key: 'enabled', label: 'Status', sortable: true, get: u => (u.enabled ? 0 : 1), render: u => (u.enabled ? badge('active') : badge('disabled', 'blocked')) },
        { key: 'two_step', label: 'Two-step', sortable: true, get: u => (u.two_step ? 0 : 1), render: u => (u.two_step ? badge('active', 'on') : '<span class="text-dim">—</span>') },
        { key: 'mcp', label: 'MCP', sortable: true, get: u => (u.mcp_access ? 0 : 1), render: u => (u.mcp_access ? badge('active', 'on') : '<span class="text-dim">off</span>') },
        { key: 'date_created', label: 'Added', sortable: true, type: 'date', mono: true, render: u => esc(formatDateTime(u.date_created)) },
      ],
      rows, rowKey: 'id', sort: this.querySelector('arc-data-table').sort ?? { key: 'role', dir: 'asc' }, unit: 'people',
      empty: { icon: 'users', title: 'Nobody matches', text: this.#q ? `No one here matches “${this.#q}”.` : 'Try another filter.' },
    });
  }

  /** The Communities cell: an Admin opens every one; a User's as chips, three then "+N". */
  #sitesCell(u) {
    if (u.role !== 'user') return '<span class="text-dim">Every community</span>';
    const sites = this.#sitesOf(u);
    if (!sites.length) return '<span class="text-dim">—</span>';
    const shown = sites.slice(0, 3);
    return `<span class="cell-chips">${chipsHtml(shown.map(m => ({ key: m.slug, label: m.name, title: levelLabel(m.permissions) })))}${sites.length > 3 ? `<span class="chip-more" title="${esc(sites.slice(3).map(m => m.name).join(', '))}">+${sites.length - 3}</span>` : ''}</span>`;
  }

  /** The communities list: a row per community with its level; the default community. */
  async #loadSites(panel, u, readOnly) {
    const box = panel.querySelector('[data-sites]');
    if (!box) return;
    let data;
    try { data = await api.users.sites(u.id); } catch (err) { box.innerHTML = `<p class="text-muted">${esc(err.message)}</p>`; return; }
    const have = new Map(data.items.map(i => [i.site_id, i.permissions]));
    box.innerHTML = `
      <div class="user-sites">${data.all_sites.map(s => {
        const perms = have.get(s.id) ?? [];
        const level = have.has(s.id) ? levelOf(perms) : 'none';
        return `<div class="user-site-row" data-site="${s.id}" data-level="${level}" ${level === 'custom' ? `data-custom="${esc(JSON.stringify(perms))}"` : ''}>
          <div class="user-site-name"><span>${esc(s.name)}</span>${level === 'custom' ? `<span class="user-site-custom" title="A set from the beta that is none of the five levels; pick one to replace it">${esc(perms.map(p => SHORT[p] ?? p).join(' + '))} · custom</span>` : ''}</div>
          <arc-segmented data-level></arc-segmented>
        </div>`; }).join('')}</div>
      <label class="form-field"><span>Opens on</span><arc-picker data-default-site></arc-picker></label>
      <p class="field-hint">Manage is everything in the community; Parcels the lots, dwellings and website data; ARB manage reviews and approves; ARB view only looks. None removes the community.</p>`;
    for (const row of box.querySelectorAll('.user-site-row')) {
      const seg = row.querySelector('arc-segmented');
      seg.configure({
        name: `Access in ${row.querySelector('.user-site-name span').textContent}`, size: 'sm',
        options: Object.entries(LEVELS).map(([value, l]) => ({ value, label: l.label, title: l.title, disabled: readOnly })),
        value: row.dataset.level,          // 'custom' matches no segment: nothing pressed until a level is picked
      });
      seg.addEventListener('change', e => { row.dataset.level = e.detail.value; delete row.dataset.custom; row.querySelector('.user-site-custom')?.remove(); });
    }
    box.querySelector('[data-default-site]').configure({
      name: 'default_site_id', options: data.all_sites.map(s => ({ value: String(s.id), label: s.name })),
      value: data.default_site_id ? String(data.default_site_id) : null,
      placeholder: 'The first community they may open', allowClear: true, searchable: data.all_sites.length > 8, disabled: readOnly,
    });
    box.dataset.loaded = '1';
  }

  /** What the drawer sends for the communities: every row with a level (custom kept as stored). */
  static #sitesBody(box, f) {
    return {
      sites: [...box.querySelectorAll('.user-site-row')]
        .map(row => ({ site_id: Number(row.dataset.site), permissions: row.dataset.custom ? JSON.parse(row.dataset.custom) : (LEVELS[row.dataset.level]?.perms ?? []) }))
        .filter(x => x.permissions.length),
      default_site_id: f.default_site_id?.value || null,
    };
  }

  /** The role <select>, offering only what this person may grant. */
  #roleField(current, disabled, note) {
    const opts = Object.entries(ROLES)
      .filter(([value]) => value !== 'superadmin' || this.#isSuper || current === 'superadmin')
      .map(([value, r]) => `<option value="${value}" ${value === current ? 'selected' : ''}>${esc(r.label)}</option>`)
      .join('');
    return `
      <label class="form-field"><span>Role</span>
        <select name="role" ${disabled ? 'disabled' : ''}>${opts}</select></label>
      <p class="field-hint" data-role-does>${esc(note ?? ROLES[current]?.does ?? '')}</p>`;
  }

  #passwordFields(required) {
    return `
      <label class="form-field"><span>${required ? 'Password *' : 'New password'}</span>
        <input name="password" type="password" autocomplete="new-password" ${required ? 'required' : ''}></label>
      <wdc-password-meter for="password"></wdc-password-meter>
      <label class="form-field"><span>${required ? 'Again *' : 'New password again'}</span>
        <input name="again" type="password" autocomplete="new-password" ${required ? 'required' : ''}></label>`;
  }

  /** The role hint follows the select. */
  #wireRoleHint(panel) {
    const select = panel.querySelector('select[name="role"]');
    select?.addEventListener('change', () => {
      panel.querySelector('[data-role-does]').textContent = ROLES[select.value]?.does ?? '';
    });
  }

  /** null if the typed password may be sent (or none was typed); else why not. */
  async #passwordRefusal(f, meter) {
    if (!f.password.value) return null;
    if (f.password.value !== f.again.value) return 'Those two passwords do not match';
    return meter.check();
  }

  #open(id) {
    const u = this.#items.find(x => x.id === id);
    if (!u) return;
    const self = u.id === this.#me.id;
    const local = !u.ldap_imported;
    const readOnly = u.role === 'superadmin' && !this.#isSuper;

    const source = local
      ? 'Local — exists only in ARC, with its own password.'
      : 'Directory — name, email and password come from the directory.';

    const { panel, close } = inspectorForm({
      title: u.login,
      width: 560,
      submitLabel: 'Save',
      body: `
        <dl class="kv">
          <dt>Name</dt><dd>${esc(u.full_name || '—')}</dd>
          <dt>Email</dt><dd>${esc(u.email || '—')}</dd>
          <dt>Source</dt><dd>${source}</dd>
          <dt>Added</dt><dd class="mono">${esc(formatDateTime(u.date_created))}</dd>
        </dl>
        ${readOnly ? '<p class="alert alert-info">Only a Super Admin can change a Super Admin.</p>' : ''}
        <h3>Access</h3>
        ${this.#roleField(u.role, self || readOnly, self ? 'You cannot change your own role.' : null)}
        <div class="toggle-row">
          <div>
            <strong>Can sign in</strong>
            <span class="toggle-note">${self ? 'You cannot block yourself.' : local
              ? 'Off: this account cannot sign in to ARC.'
              : 'Off: they cannot sign in to ARC. Their mail, file shares and Wi-Fi are not affected; '
                + 'to stop those, disable their directory account.'}</span>
          </div>
          <label class="switch"><input type="checkbox" name="enabled" ${u.enabled ? 'checked' : ''}
            ${self || readOnly ? 'disabled' : ''}><span class="slider"></span></label>
        </div>
        <div class="toggle-row">
          <div>
            <strong>MCP access</strong>
            <span class="toggle-note">Lets ${self ? 'you' : 'them'} connect Claude or another MCP client to ARC, signing in as ${self ? 'yourself' : 'themselves'} with the same rights. Off for everyone until turned on here; turning it off cuts a connected client off at once.</span>
          </div>
          <label class="switch"><input type="checkbox" name="mcp_access" ${u.mcp_access ? 'checked' : ''}
            ${readOnly ? 'disabled' : ''}><span class="slider"></span></label>
        </div>
        <h3>Communities</h3>
        <p class="text-muted">${u.role === 'user' ? 'Which communities they may open, and what they may do in each.' : 'An Admin opens every community; the rows below only matter if the role changes.'}</p>
        <div data-sites><div class="loading-center"><div class="spinner spinner-sm"></div></div></div>
        ${u.two_step || u.role !== 'user' ? `<h3>Two-step sign-in</h3>
        ${u.two_step ? `
          <p class="text-muted">On: signing in takes ${self ? 'your' : 'their'} password and a code from ${self ? 'your' : 'their'} authenticator app.
            ${self ? 'Turn yours off from <a href="#/me">My account</a>.'
              : readOnly ? '' : 'If they have lost the phone and their recovery codes, turn it off; they sign in with the password and can set it up again.'}</p>
          ${!self && !readOnly ? `<button type="button" class="btn btn-danger-ghost" data-two-step-off>Turn off two-step sign-in</button>` : ''}`
        : `<p class="text-muted">Off. ${self ? 'Turn it on from <a href="#/me">My account</a>.' : 'Each person turns their own on, from My account.'}</p>`}` : ''}
        ${local && !readOnly ? `
          <h3>Details</h3>
          <div class="form-grid-2">
            <label class="form-field"><span>First name</span><input name="first_name" value="${esc(u.first_name)}"></label>
            <label class="form-field"><span>Last name</span><input name="last_name" value="${esc(u.last_name)}"></label>
          </div>
          <label class="form-field"><span>Email</span><input name="email" type="email" value="${esc(u.email)}"></label>
          ${this.#passwordFields(false)}
          <p class="field-hint">Leave the password blank to keep the current one.</p>` : ''}
        ${!self && !readOnly ? `
          <h3>${local ? 'Delete' : 'Remove from this list'}</h3>
          <p class="text-muted">${local
            ? 'Removes this local account and its sessions for good.'
            : 'Removes this row, its role and its sessions. Their directory account is not touched; '
              + 'if they sign in again they get a new row, as a User.'}</p>
          <button type="button" class="btn btn-danger-ghost" data-delete>${local ? 'Delete' : 'Remove'} ${esc(u.login)}</button>` : ''}`,
      onSubmit: async (f) => {
        if (readOnly) return;
        const body = {};
        if (!f.role.disabled && f.role.value !== u.role) body.role = f.role.value;
        if (!f.enabled.disabled && f.enabled.checked !== u.enabled) body.enabled = f.enabled.checked;
        if (f.mcp_access && !f.mcp_access.disabled && f.mcp_access.checked !== !!u.mcp_access) body.mcp_access = f.mcp_access.checked;
        if (local) {
          for (const k of ['first_name', 'last_name', 'email']) {
            if (f[k].value.trim() !== (u[k] ?? '')) body[k] = f[k].value.trim();
          }
          const refusal = await this.#passwordRefusal(f, meter);
          if (refusal) throw new Error(refusal);
          if (f.password.value) body.password = f.password.value;
        }
        const sitesBox = panel.querySelector('[data-sites]');
        const sitesBody = sitesBox?.dataset.loaded ? ArcUsers.#sitesBody(sitesBox, f) : null;
        if (sitesBody && !readOnly) await api.users.setSites(u.id, sitesBody);
        if (!Object.keys(body).length) { if (sitesBody) { toast(`${u.login} saved`, 'success'); this.#load(); } return; }

        if (body.role === 'superadmin') {
          const ok = await modalConfirm(`Make ${u.login} a Super Admin?`, {
            title: 'Grant Super Admin', confirmText: 'Make Super Admin',
            details: 'They will be able to change who can reach this console, see its settings, and change other Super Admins.',
          });
          if (!ok) return false;
        }
        if (body.enabled === false) {
          const ok = await modalConfirm(`Block ${u.login} from signing in?`, {
            title: 'Block sign-in', confirmText: 'Block',
            details: local ? 'They are signed out straight away.'
              : 'They are signed out straight away, from this console and from account settings. Nothing else about their account changes.',
          });
          if (!ok) return false;
        }

        await api.users.update(u.id, body);
        if (f.password) f.password.value = f.again.value = '';   // never left in the DOM
        toast(`${u.login} saved`, 'success');
        this.#load();
      },
    });

    const meter = panel.querySelector('wdc-password-meter');
    if (meter) meter.context = [u.login, u.first_name, u.last_name];
    this.#wireRoleHint(panel);
    this.#loadSites(panel, u, readOnly);

    panel.querySelector('[data-two-step-off]')?.addEventListener('click', async () => {
      const ok = await modalConfirm(`Turn off two-step sign-in for ${u.login}?`, {
        title: 'Turn off two-step sign-in', confirmText: 'Turn off',
        details: 'They will sign in with the password alone until they set it up again. Their sessions are not affected.',
      });
      if (!ok) return;
      try {
        await api.users.turnOffTwoStep(u.id);
        close();
        toast(`Two-step sign-in is off for ${u.login}`, 'success');
        this.#load();
      } catch (err) {
        toast(err.message ?? 'Could not turn it off', 'error');
      }
    });

    panel.querySelector('[data-delete]')?.addEventListener('click', async () => {
      const ok = await modalConfirm(`${local ? 'Delete' : 'Remove'} ${u.login}?`, {
        title: local ? 'Delete local account' : 'Remove from Users', confirmText: local ? 'Delete' : 'Remove',
        details: local ? 'The account and its sessions are removed for good.'
          : 'Their row, role and sessions go. Their directory account is not touched.',
      });
      if (!ok) return;
      try {
        await api.users.delete(u.id);
        close();
        toast(`${u.login} ${local ? 'deleted' : 'removed'}`, 'success');
        this.#load();
      } catch (err) {
        toast(err.message ?? 'Could not delete it', 'error');
      }
    });
  }

  #createLocal() {
    const { panel } = inspectorForm({
      title: 'Add local account',
      submitLabel: 'Create',
      body: `
        <p class="text-muted">A local account exists only in ARC, with its own password — for when
          the directory cannot be reached. For anyone in the directory, use “Add from the directory”.</p>
        <label class="form-field"><span>Login *</span>
          <input name="login" required autocomplete="off" pattern="[A-Za-z0-9._\\-]{2,64}"
                 title="2–64 letters, digits, dots, dashes or underscores"></label>
        <div class="form-grid-2">
          <label class="form-field"><span>First name</span><input name="first_name"></label>
          <label class="form-field"><span>Last name</span><input name="last_name"></label>
        </div>
        <label class="form-field"><span>Email</span><input name="email" type="email"></label>
        ${this.#passwordFields(true)}
        ${this.#roleField('admin', false)}`,
      onSubmit: async (f) => {
        const refusal = await this.#passwordRefusal(f, meter);
        if (refusal) throw new Error(refusal);
        const made = await api.users.create({
          login: f.login.value.trim(),
          first_name: f.first_name.value.trim(),
          last_name: f.last_name.value.trim(),
          email: f.email.value.trim(),
          password: f.password.value,
          role: f.role.value,
        });
        f.password.value = f.again.value = '';
        toast(`${made.login} created`, 'success');
        this.#load();
      },
    });

    // The name rule needs the words as typed.
    const meter = panel.querySelector('wdc-password-meter');
    const context = () => { meter.context = ['login', 'first_name', 'last_name'].map(n => panel.querySelector(`[name="${n}"]`).value); };
    panel.querySelectorAll('[name="login"], [name="first_name"], [name="last_name"]')
      .forEach(input => input.addEventListener('input', context));
    this.#wireRoleHint(panel);
  }

  #importFromDirectory() {
    let seq = 0;
    const { panel } = inspectorForm({
      title: 'Add from the directory',
      width: 560,
      submitLabel: 'Add',
      body: `
        <p class="text-muted">To give someone console access before they have signed in. Anyone who
          has already signed in is on the list — change their role there instead.</p>
        <label class="form-field"><span>Find people</span>
          <input name="search" type="search" placeholder="Name, username or email" autocomplete="off"></label>
        <div data-results><p class="text-muted">Type at least two letters.</p></div>
        ${this.#roleField('admin', false)}`,
      onSubmit: async (f, form) => {
        const logins = [...form.querySelectorAll('input[name="login"]:checked')].map(c => c.value);
        if (!logins.length) throw new Error('Tick at least one person to add');
        const role = f.role.value;
        if (role === 'superadmin') {
          const ok = await modalConfirm(`Make ${logins.join(', ')} Super Admin?`, {
            title: 'Grant Super Admin', confirmText: 'Make Super Admin',
            details: 'They will be able to change who can reach this console, see its settings, and change other Super Admins.',
          });
          if (!ok) return false;
        }
        const res = await api.ldap.import({ logins, role });
        const skipped = res.skipped?.length ? ` (${res.skipped.join(', ')} already here)` : '';
        toast(`Added ${res.count} ${res.count === 1 ? 'person' : 'people'} as ${ROLES[role].label}${skipped}`, 'success');
        this.#load();
      },
    });

    const results = panel.querySelector('[data-results]');
    const search = panel.querySelector('input[name="search"]');
    // Enter in the search box searches; it must not submit the form.
    search.addEventListener('keydown', e => { if (e.key === 'Enter') e.preventDefault(); });
    search.addEventListener('input', debounce(async () => {
      const term = search.value.trim();
      const mine = ++seq;
      if (term.length < 2) {
        results.innerHTML = '<p class="text-muted">Type at least two letters.</p>';
        return;
      }
      results.innerHTML = '<div class="loading-center"><div class="spinner"></div></div>';
      try {
        const rows = await api.ldap.search({ search: term });
        if (mine !== seq) return;
        results.innerHTML = rows.length ? `
          <div class="scroll-list">${rows.slice(0, 50).map(r => {
            const here = r.status === 'imported';
            const name = `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim();
            return `
              <label class="scroll-row${here ? ' is-here' : ''}">
                <input type="checkbox" name="login" value="${esc(r.login)}" ${here ? 'disabled' : ''}>
                <span><strong>${esc(r.login)}</strong>${name ? ` — ${esc(name)}` : ''}</span>
                ${here ? badge('none', 'already here') : ''}
              </label>`;
          }).join('')}</div>
          ${rows.length > 50 ? `<p class="text-muted">The first 50 of ${rows.length}; type more to narrow it.</p>` : ''}`
          : `<p class="text-muted">No one in the directory matches ${esc(term)}.</p>`;
      } catch (err) {
        if (mine === seq) results.innerHTML = `<div class="alert alert-danger">${esc(err.message)}</div>`;
      }
    }, 250));
    this.#wireRoleHint(panel);
  }
}

customElements.define('arc-users', ArcUsers);
